import { FormSubmission } from "./form-submission";

export enum AiCardsStatus {
    Initial = "initial",
    Fetching = "fetching",
    Success = "success",
    NoResults = "no_results",
    Failed = "failed",
}

export enum FieldCategory {
    AI_SNAPSHOT = "aiSnapshot",
    INFO = "info",
    ACCOUNT = "account",
    NOTES = "notes",
    TASKS = "tasks",
    OPPORTUNITIES = "opportunities",
    KEY_CONTACTS = "keyContacts",
}

export type FieldSection = {
    fieldName: string;
    label?: string;
    value: string;
    type: string;
    url?: string;
};

export type SectionType<T extends FieldCategory | string | null = FieldCategory | string | null> = {
    category: {
        alias: T;
        name: string;
    };
    metadata?: Record<string, unknown>;
};

export type SectionInfoType = SectionType<FieldCategory.INFO> & {
    fields: FieldSection[][];
};

export type SectionAccountType = SectionType<FieldCategory.ACCOUNT> & {
    fields: FieldSection[][];
};

export type SectionNotesType = SectionType<FieldCategory.NOTES> & {
    fields: FieldSection[][];
};

export type SectionTasksType = SectionType<FieldCategory.TASKS> & {
    fields: FieldSection[][];
};

export type SectionOpportunitiesType = SectionType<FieldCategory.OPPORTUNITIES> & {
    fields: FieldSection[][];
};

export type SectionKeyContactsType = SectionType<FieldCategory.KEY_CONTACTS> & {
    fields: FieldSection[][];
};

export type SectionAiSnapshotType = {
    category: {
        alias: FieldCategory.AI_SNAPSHOT;
        name: string;
    };
    fields: FieldSection[][];
};

export type SectionAiInsightsType = SectionType<string> & {
    fields: FieldSection[][];
};

/** Known CRM accordion sections (excludes snapshot and custom AI insight aliases). */
export type CrmSectionType =
    | SectionInfoType
    | SectionAccountType
    | SectionNotesType
    | SectionTasksType
    | SectionOpportunitiesType
    | SectionKeyContactsType;

export type SectionsType = SectionAiSnapshotType | SectionAiInsightsType | CrmSectionType;

export type AiCardResponse = {
    consumerId: number;
    consumerParentId: number;
    sections: SectionsType[];
    profileUrl: string;
    message: string;
};

export const INTELLIGENCE_CONSUMER_TYPE = "ulcFormElement" as const;

export type IntelligenceEnrichInfo = {
    /**
     * Metadata about all data. event info like captured by, station name, rating (Submission level), event name, event date, event time, event location, etc.
     */
    metadata?: {
        eventName: string;
        eventDate: string;
        eventLocation: string;
        capturedBy: string;
        stationName: string;
        rating: string;
    };
    /**
     * Captured data: all data filled in the capture form.
     */
    capturedData?: FormSubmission["data"][];
};

export type IntelligenceEnrichField = {
    lLFieldName: string;
    value: string;
    lLFieldId: number;
    lLFieldIdentifier: string;
};

export type IntelligenceEnrichRequestBody = {
    consumerId: number;
    consumerParentId: number;
    consumerType: typeof INTELLIGENCE_CONSUMER_TYPE;
    fields: IntelligenceEnrichField[];
    info?: IntelligenceEnrichInfo;
};

export type IntelligenceEnrichParams = {
    elementId: number;
    formId: number;
    fields: IntelligenceEnrichField[];
    info?: IntelligenceEnrichInfo;
};

const CRM_FIELD_CATEGORIES = new Set<string>([
    FieldCategory.INFO,
    FieldCategory.ACCOUNT,
    FieldCategory.NOTES,
    FieldCategory.TASKS,
    FieldCategory.OPPORTUNITIES,
    FieldCategory.KEY_CONTACTS,
]);

const KNOWN_NON_AI_INSIGHT_CATEGORIES = new Set<string>([FieldCategory.AI_SNAPSHOT, ...CRM_FIELD_CATEGORIES]);

export function isSectionAiInsightsType(section: SectionsType): section is SectionAiInsightsType {
    const alias = section.category.alias;
    return alias != null && !KNOWN_NON_AI_INSIGHT_CATEGORIES.has(alias);
}

export function isCrmSectionType(section: SectionsType): section is CrmSectionType {
    const alias = section.category.alias;
    return alias != null && CRM_FIELD_CATEGORIES.has(alias);
}

/**
 * Flattens a CRM card response's `sections` into the property paths used by on-input action
 * conditions (`property_path`), each as a string:
 * - `<alias>.fields.<fieldName>` → value of the field with that `fieldName` (first row that has it)
 * - `<alias>.metadata.<key>[.<key>…]` → the section's (possibly nested) metadata value
 *
 * @example
 * flattenCrmSections(response.sections);
 * // => { "info.fields.firstName": "Jane", "opportunities.metadata.totalCount": "3",
 * //      "opportunities.metadata.highestDeal.name": "ABC Deal", ... }
 */
export function flattenCrmSections(sections: SectionsType[] | null | undefined): Record<string, string> {
    const out: Record<string, string> = {};
    for (const section of sections ?? []) {
        const alias = section.category.alias;
        if (!alias) {
            continue;
        }
        for (const field of section.fields.flat()) {
            const path = `${alias}.fields.${field.fieldName}`;
            if (!(path in out)) {
                out[path] = toScalarString(field.value);
            }
        }
        if ("metadata" in section && section.metadata) {
            addMetadataPaths(out, `${alias}.metadata`, section.metadata);
        }
    }
    return out;
}

/**
 * Resolves one property path (see {@link flattenCrmSections}) against a CRM card response's
 * `sections`; `""` when the path, section or card is missing.
 *
 * @example
 * resolveCrmPropertyPath(response.sections, "info.fields.firstName"); // => "Jane"
 */
export function resolveCrmPropertyPath(sections: SectionsType[] | null | undefined, propertyPath: string): string {
    return flattenCrmSections(sections)[propertyPath] ?? "";
}

function addMetadataPaths(out: Record<string, string>, path: string, metadata: Record<string, unknown>): void {
    for (const [key, value] of Object.entries(metadata)) {
        if (value && typeof value === "object" && !Array.isArray(value)) {
            addMetadataPaths(out, `${path}.${key}`, value as Record<string, unknown>);
        } else {
            out[`${path}.${key}`] = toScalarString(value);
        }
    }
}

function toScalarString(value: unknown): string {
    if (typeof value === "string") {
        return value;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
        return String(value);
    }
    return "";
}
